import {QueryOptions} from '@apollo/client';

import {getApolloClient} from 'graphql-client/ApolloClient';
import initializeWatchQuery from 'graphql-client/initializeWatchQuery';
import {asJQueryPromise} from 'util/Promises';

import FileState from './FileState';

interface BaseAttributes {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [attributeName: string]: any;
}
interface ApolloFileState<TQuery, TVariables extends {}, TAttributes extends BaseAttributes>
  extends FileState {
  constructor: typeof ApolloFileState;
  get<K extends keyof TAttributes>(key: K): TAttributes[K];
}

class ApolloFileState<TQuery, TVariables extends {}, TAttributes> extends FileState {
  /**
   * This class provides a way to inject/subscribe to state from Apollo in our Backbone models.
   *
   * Subclasses should specify the options for the query via a static getQueryOptions function, which should
   * minimally be of type {query: DocumentNode}. The query should be one that is fired by a child component,
   * and should return variables (like name and URL) that backbone needs to update the workspace tabs. By
   * default, watchQuery uses the `cache-first` policy, so if child components have already populated the
   * cache, the cache will be subscribed to and no network requests will be made.
   *
   * Subclasses should also specify a dataToAttributes function that maps the query data to FileState
   * attributes. This will be called when the query state changes. They can override the onChange function's
   * default behavior.
   *
   * For example:
   * ```
   * class AnalyticsDashboardFileState extends ApolloFileState {
   *   static getQueryOptions(fileId: string): QueryOptions<GetAnalyticsDashboardVariables> {
   *     return {query: GET_ANALYTICS_DASHBOARD_QUERY, variables: {analyticsDashboardId: fileId}};
   *   }
   *
   *   static dataToAttributes(data: GetAnalyticsDashboard): AnalyticsDashboardFileStateAttributes {
   *     const {analyticsDashboard} = data;
   *     return isInaccessible(analyticsDashboard)
   *       ? {}
   *       : {name: analyticsDashboard.name, url: analyticsDashboard.url};
   *   }
   *
   *   initialize(...args: Array<unknown>): void {
   *     super.initialize(...args);
   *     // Do rest of initialization
   *   }
   * }
   * ```
   */

  static getQueryOptions(fileId: string): QueryOptions {
    throw new Error('Not implemented');
  }

  // Replace `any` with class-type parameters (e.g., `TAttributes`) after
  // https://github.com/microsoft/TypeScript/issues/33892 is resolved
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static dataToAttributes(data: any): any {
    throw new Error('Not implemented');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static openFile(fileId: string): JQueryPromise<ApolloFileState<any, any, any>> {
    // Wait on query before returning FileState, so that it's initialized with the query's data
    return asJQueryPromise(getApolloClient().query(this.getQueryOptions(fileId))).then(
      (response) =>
        new this({
          id: fileId,
          ...(response != null ? this.dataToAttributes(response.data) : {}),
        })
    );
  }

  initialize(...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(...args);
    const queryOptions = this.constructor.getQueryOptions(this.id) as QueryOptions<TVariables>;
    const querySubscription = initializeWatchQuery<TQuery>(queryOptions, this.onChange.bind(this));
    this.set({
      unsubscribeApollo: () => {
        querySubscription.unsubscribe();
      },
    });
  }

  onChange(data: TQuery, loading: boolean): void {
    if (loading) {
      return;
    }
    this.set(this.constructor.dataToAttributes(data) as TAttributes);
  }

  cleanUp(): void {
    this.get('unsubscribeApollo')();
  }
}

export default ApolloFileState;
